import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { MatchRole, RefereeCertification, resolveEnum } from '@/models/tournament';
import type { MatchView, RefereeView } from '@/models/tournament';
import { parseApiWallClock } from '@/utils/apiDate';

interface AssignmentActionPanelProps {
  selectedMatch: MatchView | null;
  selectedReferee: RefereeView | null;
  onAssign: () => void;
}

/**
 * The API serializes enums as STRINGS ("FIFAInternational"), but the label/colour maps below are
 * keyed by the NUMERIC enum. Indexing them with the raw wire value silently yields undefined, so
 * the certification badge rendered blank (or "Unknown") on every row. resolveEnum bridges both.
 */
const certOf = (v: unknown) => resolveEnum(RefereeCertification, v) as RefereeCertification;

const certificationKeys: Record<RefereeCertification, string> = {
  [RefereeCertification.NationalLevelA]: 'National Level A',
  [RefereeCertification.AFCElite]: 'AFC Elite',
  [RefereeCertification.FIFAInternational]: 'FIFA International',
};

const AssignmentActionPanel: React.FC<AssignmentActionPanelProps> = ({ selectedMatch, selectedReferee, onAssign }) => {
  const { t, i18n } = useTranslation('tournament');

  const roleKeys: Record<MatchRole, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const canAssign = selectedMatch && selectedReferee;

  // Check for conflicts
  const hasConflict = false; // Simplified: real conflict checking done server-side

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-4" data-testid="assignment-action-panel">
        <h2 className="text-lg font-semibold text-foreground flex items-center mb-4">
          <Icon name="CheckSquare" size={20} className="me-2 text-primary" />
          {t('Assignment Actions')}
        </h2>

        {!selectedMatch && !selectedReferee && (
          <div className="text-center py-8">
            <Icon name="MousePointerClick" size={48} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground mb-1">{t('Select a match and referee')}</p>
            <p className="text-xs text-muted-foreground/80">{t('to proceed with assignment')}</p>
          </div>
        )}

        {selectedMatch && !selectedReferee && (
          <div className="text-center py-8">
            <Icon name="User" size={48} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground mb-1">{t('Match selected')}</p>
            <p className="text-xs text-muted-foreground/80">{t('Now select a referee to assign')}</p>
          </div>
        )}

        {!selectedMatch && selectedReferee && (
          <div className="text-center py-8">
            <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground mb-1">{t('Referee selected')}</p>
            <p className="text-xs text-muted-foreground/80">{t('Now select a match to assign')}</p>
          </div>
        )}

        {canAssign && (
          <div className="space-y-4">
            {/* Assignment Preview — was panel card > gradient box > two white cards, four surfaces
                deep for six values. One neutral block, two labelled rows. The gradient also leaned
                on the exact primary hue, which is overridden per tenant at runtime. */}
            <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-4">
              <p className="text-xs font-medium text-muted-foreground">{t('Assignment Preview')}</p>

              <div>
                <div className="flex items-center mb-1.5">
                  <Icon name="Calendar" size={14} className="text-primary me-2" />
                  <p className="text-xs font-medium text-muted-foreground">{t('Match')}</p>
                </div>
                <p className="text-sm font-semibold text-foreground">
                  {selectedMatch?.team1Name} {t('vs')} {selectedMatch?.team2Name}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  #{selectedMatch?.id}
                  <span className="mx-1.5 text-muted-foreground/60">·</span>
                  {parseApiWallClock(selectedMatch?.scheduledDateTime)?.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
                </p>
              </div>

              <div className="border-t border-border pt-3">
                <div className="flex items-center mb-1.5">
                  <Icon name="User" size={14} className="text-primary me-2" />
                  <p className="text-xs font-medium text-muted-foreground">{t('Referee')}</p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedReferee?.photoUrl && (
                    <img
                      src={selectedReferee.photoUrl}
                      alt={t('{{name}} profile photo', { name: selectedReferee?.name })}
                      className="w-9 h-9 rounded-full object-cover shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{selectedReferee?.name}</p>
                    {/* Was String(selectedReferee?.certification) — the raw enum, so the user
                        literally read "2" where every other surface says "FIFA International". */}
                    <p className="text-xs text-muted-foreground">
                      {selectedReferee?.certification != null
                        ? t(certificationKeys[certOf(selectedReferee.certification)] ?? 'Unknown')
                        : t('Unknown')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Conflict Detection */}
            {hasConflict && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-start">
                  <Icon name="AlertCircle" size={14} className="text-red-600 dark:text-red-400 me-2 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-red-900 dark:text-red-200 mb-1">{t('Scheduling Conflict Detected')}</p>
                    <p className="text-xs text-red-700 dark:text-red-300">
                      {t('Referee has another match scheduled on this date')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Existing Referee Team Members */}
            {selectedMatch?.refereeTeamMembers?.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <div className="flex items-start">
                  <Icon name="Users" size={14} className="text-amber-600 dark:text-amber-400 me-2 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-amber-900 dark:text-amber-200 mb-1">{t('Existing Referee Team Members')}</p>
                    <div className="space-y-1">
                      {selectedMatch.refereeTeamMembers.map((member) => (
                        <p key={member.id} className="text-xs text-amber-700 dark:text-amber-300">
                          {member.userName} ({t(roleKeys[(resolveEnum(MatchRole, member.role) as MatchRole) ?? member.role] ?? 'Unknown')})
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons. Below `lg` the three panels stack and this button lands at the very
                bottom of the page, after two long lists; the page docks an identical action to the
                bottom of the viewport there instead, so this one is `lg`-only and the CTA is never
                duplicated. */}
            <div className="space-y-2 hidden lg:block">
              <Button
                onClick={onAssign}
                disabled={hasConflict}
                fullWidth
                iconName="CheckCircle"
                data-testid="assign-referee-btn"
              >
                {t('Assign Referee')}
              </Button>

              {hasConflict && (
                <p className="text-xs text-center text-red-600 dark:text-red-400">
                  {t('Cannot assign due to scheduling conflict')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {selectedReferee && (
        <div className="bg-card border border-border rounded-lg p-4" data-testid="referee-stats-panel">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center">
            <Icon name="BarChart3" size={16} className="me-2 text-primary" />
            {t('Referee Stats')}
          </h3>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon name="Award" size={14} className="text-muted-foreground/70 me-2" />
                <span className="text-xs text-muted-foreground">{t('Total Matches')}</span>
              </div>
              <span className="text-sm font-semibold text-foreground">{selectedReferee?.totalMatches}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon name="Star" size={14} className="text-yellow-500 me-2" />
                <span className="text-xs text-muted-foreground">{t('Performance Rating')}</span>
              </div>
              <span className="text-sm font-semibold text-foreground">{selectedReferee?.rating}/5.0</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Icon name="MapPin" size={14} className="text-muted-foreground/70 me-2" />
                <span className="text-xs text-muted-foreground">{t('Location')}</span>
              </div>
              <span className="text-sm font-semibold text-foreground">{selectedReferee?.location}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentActionPanel;
